export function randomInt(min: number, max: number, rng: () => number = Math.random): number {
	return Math.floor(rng() * (max - min + 1)) + min;
}

export function randomFloat(min: number, max: number, rng: () => number = Math.random): number {
	return rng() * (max - min) + min;
}

export function randomChance(probability: number, rng: () => number = Math.random): boolean {
	if (probability <= 0) return false;
	return rng() < probability;
}

export function randomElement<T>(array: T[], rng: () => number = Math.random): T | null {
	if (array.length === 0) return null;
	return array[Math.floor(rng() * array.length)];
}

export function shuffleArray<T>(array: T[], rng: () => number = Math.random): T[] {
	const a = [...array];
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(rng() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
}

export function weightedRandom<T>(weights: Map<T, number>, rng: () => number = Math.random): T {
	if (weights.size === 0) throw new Error("weightedRandom called with empty map");
	let sumOfAllShares = 0;
	for (const share of weights.values()) sumOfAllShares += share;
	const random = rng() * sumOfAllShares;
	let currentSum = 0;
	for (const [key, value] of weights) {
		currentSum += value;
		if (random <= currentSum) return key;
	}
	// Fallback (shouldn't happen due to floating point)
	return weights.keys().next().value!;
}

export function weightedRandomPickN<T>(weights: Map<T, number>, n: number, rng: () => number = Math.random): Set<T> {
	const result = new Set<T>();
	const remaining = new Map(weights);
	while (result.size < n && remaining.size > 0) {
		const pick = weightedRandom(remaining, rng);
		result.add(pick);
		remaining.delete(pick);
	}
	return result;
}

export function sfc32(a: number, b: number, c: number, d: number): () => number {
	return function() {
		a |= 0; b |= 0; c |= 0; d |= 0;
		let t = (a + b | 0) + d | 0;
		d = d + 1 | 0;
		a = b ^ b >>> 9;
		b = c + (c << 3) | 0;
		c = (c << 21 | c >>> 11);
		c = c + t | 0;
		return (t >>> 0) / 4294967296;
	};
}

export function cyrb128(str: string): [number, number, number, number] {
	let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
	for (let i = 0, k; i < str.length; i++) {
		k = str.charCodeAt(i);
		h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
		h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
		h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
		h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
	}
	h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
	h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
	h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
	h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
	h1 ^= (h2 ^ h3 ^ h4), h2 ^= h1, h3 ^= h1, h4 ^= h1;
	return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}

export function seededRng(key: string): () => number {
	const s = cyrb128(key);
	return sfc32(s[0], s[1], s[2], s[3]);
}

export function generateSeedString(length: number = 8): string {
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
	let result = '';
	for (let i = 0; i < length; i++) {
		result += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return result;
}
